// Reine Logik für „Headset prüfen“ (ohne Browser-APIs, mit node --test prüfbar): Pegel in dBFS, Urteil über das
// Mikrofon, verständliche Fehlertexte, Zusammenfassung für den Punkt am Knopf oben rechts.

export type Art = "ok" | "gelb" | "rot" | "warte";

export interface Urteil {
  art: Art;
  text: string;
}

/** Ab diesem Spitzenpegel gilt das Mikrofon als funktionierend (Sprechen ins Headset liegt bei etwa -35 bis -10 dBFS). */
export const SCHWELLE_OK = -50;
/** Darunter ist das Signal digital still: das Mikrofon liefert nur Nullen (stummgeschaltet, Funk weg, Kabel). */
export const SCHWELLE_STILLE = -90;
/** So lange wartet der Test auf ein „Hallo“, bevor er urteilt. */
export const WARTEZEIT_MS = 4000;

/** Effektivwert eines Ausschnitts in dBFS (-Infinity bei reiner Stille). */
export function pegelDb(proben: ArrayLike<number>): number {
  if (!proben.length) return -Infinity;
  let summe = 0;
  for (let i = 0; i < proben.length; i++) summe += proben[i] * proben[i];
  const rms = Math.sqrt(summe / proben.length);
  return rms > 0 ? 20 * Math.log10(rms) : -Infinity;
}

/** dBFS -> Balkenbreite 0..1 (-70 dBFS leer, -10 dBFS voll). */
export function pegelAnteilVon(db: number): number {
  if (!Number.isFinite(db)) return 0;
  return Math.max(0, Math.min(1, (db + 70) / 60));
}

export function mikrofonUrteil(e: { spitzeDb: number; laufzeitMs: number; stumm?: boolean }): Urteil {
  if (e.stumm) return { art: "rot", text: "Mikrofon ist stummgeschaltet. Stummtaste am Headset oder im System lösen." };
  if (e.spitzeDb >= SCHWELLE_OK) return { art: "ok", text: "Mikrofon funktioniert." };
  if (e.laufzeitMs < WARTEZEIT_MS) return { art: "warte", text: "Sprich kurz „Hallo“ ins Headset …" };
  if (!Number.isFinite(e.spitzeDb) || e.spitzeDb < SCHWELLE_STILLE)
    return {
      art: "rot",
      text: "Mikrofon sendet nichts – Headset prüfen: Stecker bzw. Funk-Empfänger, Stummschalter am Kabel, oder oben ein anderes Mikrofon wählen.",
    };
  return { art: "gelb", text: "Kaum etwas zu hören. Sprich „Hallo“ ins Headset. Bleibt der Balken unten: Mikrofonarm näher an den Mund oder anderes Mikrofon wählen." };
}

/** getUserMedia-Fehler -> was zu tun ist. */
export function mikrofonFehlerText(name: string | undefined, meldung?: string): string {
  switch (name) {
    case "NotAllowedError":
    case "SecurityError":
      return "Mikrofon ist für diese Seite gesperrt. Im Browser links in der Adresszeile auf das Schloss klicken und Mikrofon „Zulassen“, in der Desktop-App unter Systemeinstellungen → Datenschutz → Mikrofon erlauben. Dann „Neu prüfen“.";
    case "NotFoundError":
    case "OverconstrainedError":
      return "Kein Mikrofon gefunden. Headset anstecken bzw. Bluetooth verbinden, dann „Neu prüfen“.";
    case "NotReadableError":
    case "AbortError":
      return "Mikrofon ist belegt oder gestört (z. B. durch Teams, Zoom oder Close). Dort schließen, dann „Neu prüfen“.";
    default:
      return `Mikrofon lässt sich nicht öffnen${meldung ? `: ${meldung}` : "."}`;
  }
}

const QUALITAET: Record<string, string> = { excellent: "sehr gut", great: "sehr gut", good: "gut", fair: "mäßig", degraded: "schlecht" };

/** Ergebnis des Leitungstests (Twilio-Preflight) in einem Satz. */
export function leitungText(p: { ok: boolean; quality: string | null; mos: number | null; rtt: number | null; warnings?: string[] } | null): Urteil | null {
  if (!p) return null;
  if (!p.ok) return { art: "rot", text: `Leitungstest fehlgeschlagen${p.warnings?.length ? `: ${p.warnings[0]}` : "."}` };
  const q = p.quality ? (QUALITAET[p.quality] ?? p.quality) : "in Ordnung";
  const teile = [`Leitung ${q}`];
  if (p.mos) teile.push(`MOS ${p.mos.toFixed(1)}`);
  if (p.rtt) teile.push(`${Math.round(p.rtt)} ms`);
  return { art: p.quality === "degraded" || p.quality === "fair" ? "gelb" : "ok", text: teile.join(" · ") };
}

/** Punkt am Knopf: das schlechteste Teilergebnis zählt (rot vor gelb vor ok); ohne Ergebnis keiner. */
export function gesamtArt(teile: (Art | null | undefined)[]): Exclude<Art, "warte"> | null {
  const da = teile.filter((t): t is Art => !!t && t !== "warte");
  if (!da.length) return null;
  if (da.includes("rot")) return "rot";
  if (da.includes("gelb")) return "gelb";
  return "ok";
}
